import { useEffect, useRef, useState } from 'react';

/**
 * Detects newly-added items in an array and returns their IDs for highlighting.
 *
 * Designed for RTDB live additions (1-2 items at a time). Bulk changes are
 * silently absorbed: empty→populated, or more than MAX_HIGHLIGHT new items.
 *
 * @param items    The current array of items (e.g. merged with RTDB)
 * @param getKey   Extract a unique string key from each item
 * @param duration How long (ms) an item stays highlighted (default 1500)
 * @returns Set of currently-highlighted item IDs
 */

const MAX_HIGHLIGHT = 3;

export function useHighlightedIds<T>(
    items: T[],
    getKey: (item: T) => string,
    duration = 1500,
): Set<string> {
    const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
    const prevKeysRef = useRef<Set<string> | null>(null);

    useEffect(() => {
        const currentKeys = new Set(items.map(getKey));
        const prev = prevKeysRef.current;

        // First render or previous set was empty → bulk load. Absorb silently.
        if (!prev || prev.size === 0) {
            prevKeysRef.current = currentKeys;
            return;
        }

        // Find keys that are in current but not in previous
        const newKeys: string[] = [];
        currentKeys.forEach((key) => {
            if (!prev.has(key)) newKeys.push(key);
        });

        prevKeysRef.current = currentKeys;

        // No new items, or bulk change (date range / account switch) → absorb silently
        if (newKeys.length === 0 || newKeys.length > MAX_HIGHLIGHT) return;

        setHighlighted((prev) => {
            const next = new Set(prev);
            newKeys.forEach((k) => next.add(k));
            return next;
        });

        const timer = setTimeout(() => {
            setHighlighted((prev) => {
                const next = new Set(prev);
                newKeys.forEach((k) => next.delete(k));
                return next;
            });
        }, duration);

        return () => clearTimeout(timer);
    }, [items, getKey, duration]);

    return highlighted;
}
